import { Preferences } from "@capacitor/preferences";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { get, post } from "api";
import { useAppState } from "contexts/AppContext";
import { queryClient } from "pages/Root";
import Services from "services/Services";
import { sleep } from "toolbox/Promise";

type MultiPlatformUser = {
  userId?: string | null;
  platformLocationId: string;
  platformId?: string | null;
  phoneNumber?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  dateOfBirth?: string | null;
  signUpSource?: string | null;
  referrerName?: string | null;
  signupReferralCode?: string | null;
};

export const useCreateMultiPlatformUser = () => {
  const appState = useAppState();
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: async (user: MultiPlatformUser) => {
      const { value: anonymousToken } = await Preferences.get({
        key: "multiPlatformVerifyToken",
      });

      if (user.platformId) {
        appState.setPlatformId(user.platformId);
      }

      return post("/multi-platform/user", user, anonymousToken);
    },
    onSuccess: async ({ token, platformDomain }) => {
      if (!token) {
        throw new Error("Failed to create user.");
      }

      await Services.auth.setToken(token);
      window.localStorage.removeItem("state");

      // Time buffer to avoid concurrency issues on the server
      await sleep(600);

      const { platformDomainTokens } = await queryClient.fetchQuery({
        queryKey: ["/multi-platform/list"],
        queryFn: () => get("/multi-platform/list"),
      });

      const domainScheme = new URL(
        import.meta.env.REACT_APP_API_BASE_URL ?? location.href
      ).protocol;
      appState.setApiUrl(`${domainScheme}//${platformDomain}`);
      appState.setAppIcons(
        platformDomainTokens.map(
          ({ platformSubdomain }) => `launcher_${platformSubdomain}`
        )
      );

      await Preferences.set({
        key: "platformDomainTokens",
        value: JSON.stringify(platformDomainTokens),
      });

      queryClient.invalidateQueries();
      queryClient.resetQueries();

      const user = await queryClient.fetchQuery({
        queryKey: ["/user"],
        queryFn: () => get("/user"),
      });

      if (user.isGuest) {
        navigate("/home");
      } else {
        navigate("/signin/account-created");
      }
    },
  });

  return mutation;
};
